import { Theme } from '@coinbase/cdp-react';
import { borderRadiusMap } from 'apps/cdp-web-demo/app/data/borderRadius';
import { defaultFonts } from 'apps/cdp-web-demo/app/data/defaultFonts';
import { allFonts } from 'apps/cdp-web-demo/app/data/fonts';
import { darkTheme, lightTheme } from 'apps/cdp-web-demo/app/data/themes';
import { getHexFromAccentColor } from 'apps/cdp-web-demo/app/getHexFromAccentColor';
import {
  AccentColor,
  BorderRadius,
  ConfigProps,
  FontFamily,
  ThemeMode,
} from 'apps/cdp-web-demo/app/types';

type FontUrlMap = Record<FontFamily, string>;

const fontUrlMap: FontUrlMap = {
  inter:
    'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
  openSans:
    'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap',
  rethinkSans:
    'https://fonts.googleapis.com/css2?family=Rethink+Sans:ital,wght@0,400..800;1,400..800&display=swap',
  sourceCodePro:
    'https://fonts.googleapis.com/css2?family=Source+Code+Pro:ital,wght@0,200..900;1,200..900&display=swap',
};

export function buildTheme({
  themeMode,
  accentColor,
  borderRadius,
  fontFamily,
}: {
  themeMode: ThemeMode;
  accentColor: AccentColor;
  borderRadius: BorderRadius | undefined;
  fontFamily: FontFamily;
}): Partial<Theme> {
  const baseTheme = themeMode === 'light' ? lightTheme : darkTheme;
  const hex = getHexFromAccentColor(accentColor, themeMode);
  const t: ConfigProps['theme'] = {
    ...baseTheme,
    'colors-bg-primary': hex,
    'colors-fg-primary': hex,
  };

  if (borderRadius) {
    const conf = borderRadiusMap[borderRadius];
    t['borderRadius-banner'] = `var(--cdp-web-borderRadius-${conf.banner})`;
    t['borderRadius-cta'] = `var(--cdp-web-borderRadius-${conf.cta})`;
    t['borderRadius-link'] = `var(--cdp-web-borderRadius-${conf.cta})`;
    t['borderRadius-input'] = `var(--cdp-web-borderRadius-${conf.input})`;
    t['borderRadius-select-trigger'] = `var(--cdp-web-borderRadius-${conf.input})`;
    t['borderRadius-select-list'] = `var(--cdp-web-borderRadius-${conf.input})`;
    t['borderRadius-modal'] = `var(--cdp-web-borderRadius-${conf.modal})`;
  }

  if (fontFamily && fontFamily !== 'rethinkSans') {
    if (fontFamily !== 'sourceCodePro') {
      t['font-family-sans'] = allFonts[fontFamily].style.fontFamily;
      t['font-family-mono'] = defaultFonts.mono;
      t['font-family-body'] = 'var(--cdp-web-font-family-sans)';
      t['font-family-interactive'] = 'var(--cdp-web-font-family-sans)';
    } else {
      t['font-family-sans'] = defaultFonts.sans;
      t['font-family-mono'] = allFonts[fontFamily].style.fontFamily;
      t['font-family-body'] = 'var(--cdp-web-font-family-mono)';
      t['font-family-interactive'] = 'var(--cdp-web-font-family-mono)';
    }
    t['font-family-iframe'] =
      `${allFonts[fontFamily].style.fontFamily}, ${fontFamily === 'sourceCodePro' ? 'monospace' : 'sans-serif'}`;
    t['font-url-iframe'] = fontUrlMap[fontFamily];
  }

  return t;
}
